import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import LegalPage, { COMPANY_NAME, GOVERNING_LAW, List, P, SUPPORT_EMAIL } from './LegalPage';

const EFFECTIVE = 'October 4, 2026';

/** The Terms of Service: what JobFlo is, what we promise, what we ask, and what we are not responsible for. Plain English on purpose. */
const TermsPage: React.FC = () => (
  <LegalPage
    title="Terms of Service"
    effective={EFFECTIVE}
    intro={
      <P>
        These terms are the agreement between you and {COMPANY_NAME} ("we", "us") for the JobFlo website at app.jobflo.me,
        the JobFlo browser extension, and the services behind them (together, "JobFlo"). By creating an account or using
        JobFlo you agree to them. If you do not agree, do not use JobFlo. Our{' '}
        <Link component={RouterLink} to="/privacy">
          Privacy Policy
        </Link>{' '}
        explains what we do with your information and is part of these terms.
      </P>
    }
    sections={[
      {
        title: 'What JobFlo does',
        body: (
          <>
            <P>JobFlo helps you find and apply for jobs. It:</P>
            <List
              items={[
                'lists job openings read from employers’ own careers pages and applicant tracking systems;',
                'fills application forms on employers’ websites with the profile, answers and resume you gave it, when you ask it to;',
                'can write a version of your resume aimed at one job, built only from what your own resume and your Experience library say;',
                'tracks the applications you made and, if you turn it on, reads employer emails about them to update their status.',
              ]}
            />
            <P>
              JobFlo never submits an application. You review every form and press the employer’s own submit button yourself.
              JobFlo also never answers a question about your eligibility, background, demographics or similar on your behalf
              unless you have entered and confirmed that answer yourself.
            </P>
          </>
        ),
      },
      {
        title: 'Your account',
        body: (
          <>
            <P>
              You need an account to use most of JobFlo. You must be at least 16 years old and give us accurate information.
              You are responsible for what happens under your account and for keeping your sign-in secure. One person, one
              account. You can delete your account at any time from the Profile page, which removes your data as described in
              the Privacy Policy.
            </P>
          </>
        ),
      },
      {
        title: 'Your content',
        body: (
          <>
            <P>
              Your resume, your profile, your answers and your applications are yours. You give us the permission we need to
              store them, show them back to you, fill forms with them, send them to the model vendors that build tailored
              resumes, and deliver the emails you asked us to handle. We claim no other rights to your content. You are
              responsible for your content being accurate and yours to use.
            </P>
          </>
        ),
      },
      {
        title: 'Tailored resumes',
        body: (
          <>
            <P>
              A tailored resume is written by a language model from your own resume, what you added to your Experience
              library, and the job posting. We check every line against those and put back any line that says something they
              do not, but no check is perfect. What you add to your Experience library must be true, yours to share, and free
              of anything your employer or a client holds confidential.
              You must read a tailored resume before you send it, and you are responsible for what you send to an employer.
              Tailored resumes are deleted 30 days after they are built unless the application went further than a submission;
              see the Privacy Policy.
            </P>
          </>
        ),
      },
      {
        title: 'Email tracking',
        body: (
          <>
            <P>
              If you turn email tracking on, JobFlo reads emails that employers send about your applications, either at a JobFlo
              address you give to employers or as copies forwarded from your own mailbox, and uses them to update the status of
              the matching application. Mail sent to a JobFlo address is forwarded to your account’s email address, which must
              be confirmed as yours. We do not read any other email of yours. You can turn this off at any time; your JobFlo
              address keeps forwarding mail to you for as long as your account exists.
            </P>
          </>
        ),
      },
      {
        title: 'Acceptable use',
        body: (
          <>
            <P>You agree not to:</P>
            <List
              items={[
                'apply for jobs under a false identity, or put information you know to be false into an application;',
                'use JobFlo to send unsolicited messages, or to apply for jobs you have no intention of taking;',
                'scrape, copy or resell the job listings, or access JobFlo by any means other than the website and the extension;',
                'interfere with JobFlo, other users, or the employers’ websites it works with, or try to get around its limits;',
                'use JobFlo for anything unlawful.',
              ]}
            />
            <P>We may suspend or close an account that breaks these rules.</P>
          </>
        ),
      },
      {
        title: 'Plans, payment and trials',
        body: (
          <>
            <P>
              JobFlo has a free plan and paid plans. What each includes, and its price, is shown on the website when you choose
              one. Paid plans are billed in advance, monthly or yearly, and renew automatically until you cancel. You can cancel
              at any time; the plan stays active until the end of the period you paid for, and we do not refund partial
              periods except where the law requires it. A free trial, when offered, ends on the date shown when you start it;
              unless you cancel before then, the paid plan begins. Prices may change; we will tell you at least 30 days before a
              change affects you. Payments are handled by a payment provider; we do not store your card details.
            </P>
          </>
        ),
      },
      {
        title: 'Employers and other third parties',
        body: (
          <>
            <P>
              Job listings come from employers and their software; we do not vet them and are not part of any application or
              hiring decision. Employers’ websites have their own terms. Tailored resumes are built with language models run
              by third parties (see the Privacy Policy). We are not responsible for what employers or those third parties do.
            </P>
          </>
        ),
      },
      {
        title: 'Availability and changes',
        body: (
          <>
            <P>
              We work to keep JobFlo running, but it is provided "as is" and we do not promise it will be available at all
              times, free of errors, or that it will fill every form correctly or get you any job. We may change or
              discontinue features; if we discontinue a paid feature, we will refund the unused part of your plan for it.
            </P>
          </>
        ),
      },
      {
        title: 'Limits on our liability',
        body: (
          <>
            <P>
              To the extent the law allows: we are not liable for indirect, incidental or consequential losses, including a
              lost job opportunity; and our total liability to you for anything arising from JobFlo is limited to the amount
              you paid us in the 12 months before the claim, or $50 if you paid nothing. Nothing in these terms limits
              liability that cannot be limited by law.
            </P>
          </>
        ),
      },
      {
        title: 'Ending the agreement',
        body: (
          <>
            <P>
              You can stop using JobFlo and delete your account at any time. We can close your account if you break these
              terms or if we discontinue JobFlo, with notice where we reasonably can. Sections 3, 9, 10 and 12 survive.
            </P>
          </>
        ),
      },
      {
        title: 'Governing law and disputes',
        body: (
          <>
            <P>
              These terms are governed by the laws of {GOVERNING_LAW}, without regard to conflict-of-law rules. If you have a
              problem, write to us first at {SUPPORT_EMAIL} and we will try to resolve it; otherwise disputes go to the courts
              of {GOVERNING_LAW}. If you live somewhere whose law gives you rights these terms cannot take away, those rights
              stand.
            </P>
          </>
        ),
      },
      {
        title: 'Changes to these terms',
        body: (
          <>
            <P>
              We may update these terms. For a material change we will tell you by email or in JobFlo at least 14 days before
              it takes effect. Using JobFlo after that date means you accept the new terms.
            </P>
          </>
        ),
      },
      {
        title: 'Contact',
        body: <P>Questions about these terms: {SUPPORT_EMAIL}.</P>,
      },
    ]}
  />
);

export default TermsPage;
